/**
 * Shared review-grid constants — column groups and the per-platform
 * editable / editor-type sets.
 *
 * Extracted from CampaignLaunchWorkflow.js so the Launch-board campaign
 * details page (LaunchCampaignDetails) can drive the same cell detail
 * panel and inline editors from the same definitions. Keeping one copy
 * matters: the editable sets gate whether a user is shown a "saved"
 * state, so a drifted duplicate would silently promise persistence the
 * save path doesn't deliver.
 */

// GT's own column set + read-only list live next to the rest of the
// GroundTruth constants; the editable set is DERIVED from them here so all
// four platforms' allow-lists are built by one function. (Import direction:
// gtConstants never imports this module, so there is no cycle.)
import { GT_GROUP_COLS, GT_NON_EDITABLE, GT_LIST_FIELDS, GT_EDITABLE_FIELDS } from './groundtruth/gtConstants';
// The parent-scaled arithmetic the appliers write through — reused verbatim by
// the SCALED_FIELDS previews below so disclosure and write can't diverge.
import { scaleToParent, dv360ImpsFromBudget } from './sharedFieldWriters';

// Column groups — driven by real DV360 parsed_response fields. Each row is
// one line item; sticky columns on the left (idx / name / channel) live
// outside these groups.
export const GROUP_COLS = {
  overview:  [{k:'fmt',l:'Format',w:80},{k:'package',l:'Package',w:150},{k:'category',l:'Category',w:130},{k:'tactic',l:'Tactic',w:140},{k:'start',l:'Start',w:96,fmt:'date'},{k:'end',l:'End',w:96,fmt:'date'},{k:'cpm',l:'CPM',w:64,fmt:'cur'},{k:'imps',l:'Impressions',w:115,fmt:'num'},{k:'budget',l:'Budget',w:100,fmt:'cur'}],
  flighting: [{k:'start',l:'Start',w:110,fmt:'date'},{k:'end',l:'End',w:110,fmt:'date'},{k:'flightType',l:'Flight type',w:160},{k:'dailyCap',l:'Daily cap',w:104,fmt:'num'},{k:'pacing',l:'Pacing',w:150},{k:'entityStatus',l:'Status',w:90}],
  budget:    [{k:'package',l:'Package',w:150},{k:'cpm',l:'CPM',w:72,fmt:'cur'},{k:'imps',l:'Impressions',w:120,fmt:'num'},{k:'budget',l:'Budget',w:110,fmt:'cur'},{k:'dailyCap',l:'Daily cap',w:96,fmt:'num'},{k:'pacing',l:'Pacing model',w:150}],
  targeting: [{k:'tactic',l:'Tactic',w:140},{k:'geo',l:'Geography',w:200},{k:'audiences',l:'Audiences',w:180},{k:'pmp',l:'PMP / placement',w:160},{k:'interests',l:'Interests',w:160},{k:'keywords',l:'Keywords',w:200},{k:'expansion',l:'Expansion',w:160},{k:'devices',l:'Devices',w:160},{k:'environment',l:'Environment',w:140},{k:'freqCap',l:'Freq cap',w:130}],
  creative:  [{k:'creative',l:'Asset',w:120},{k:'creativeFormat',l:'Sizes',w:220},{k:'clickURL',l:'Click URL',w:200},{k:'impTracker',l:'Tracker',w:140}],
  // bidFloor is 'Base bid' in TTD semantics (ag.BaseBidCPM); on DV360 the
  // same cell carries the fixed/max-average bid, so the label reads as the
  // line item's bid there too. bidCeiling (ag.MaxBidCPM) is TTD-only —
  // DV360 rows render '—' for it.
  bid:       [{k:'bidStrategy',l:'Strategy',w:170},{k:'bidFloor',l:'Base bid',w:96},{k:'bidCeiling',l:'Max bid',w:96},{k:'bidGoal',l:'Goal',w:120},{k:'viewability',l:'Viewability',w:104},{k:'brandSafety',l:'Brand safety',w:170}],
  tracking:  [{k:'pixel',l:'Pixel / tag',w:200},{k:'kpi',l:'KPI',w:90},{k:'kpiTarget',l:'KPI target',w:120},{k:'convWindow',l:'Lookback',w:200}],
};
export const GROUP_KEYS = Object.keys(GROUP_COLS);

// Fields that round-trip through saveReviewEdits → planning-review PUT.
// The inline editor only renders for cells in this set so users never see
// a "saved" state for an edit that wasn't actually persisted.
//
// EDITABLE BY DEFAULT: every grid column is editable unless a platform's
// denylist names it. reviewEdits.js must carry a writer for every scalar in
// the derived set — the round-trip tests in reviewEdits.test.js enforce
// that per field. List-shaped cells (geo / audiences / keywords / interests
// / pmp) stay in the set on purpose: CellDetailPanel routes them (and any
// ' · '-joined value) to the list renderer instead of the scalar editor,
// and being "editable" is what enables their per-item delete flows.
export const editableFieldsFor = (groupCols, denylist) => new Set(
  ['name', ...Object.values(groupCols).flat().map((c) => c.k)]
    .filter((k) => !denylist.has(k)),
);

// Read-only columns per platform — each entry says WHY there is no write
// target the launcher would consume.
//
// `entityStatus` is denylisted on EVERY platform for a different reason than
// the rest: there IS a write target, and that is the problem. ACTIVATION IS
// THE LAUNCH ACTION'S JOB, NOT A CELL. A plan-review surface exists to check a
// plan before it goes live, so it must not be able to promote an entity INTO a
// live state — one keystroke in a Status cell should never be the difference
// between a paused draft and a campaign that is spending. TTD makes the stakes
// concrete: copy_ttd_campaign_launcher builds copied ad groups with
// `IsEnabled: False` ("copy is created paused/disabled"), but that is
// BUILD-time — a grid edit is applied afterwards and `IsEnabled` rides
// straight into POST /adgroup (ttd_adgroup_creator does `body =
// dict(adgroup_cfg)` and pops only TargetProfiles/Creatives), and
// ttd_campaign_creator has no pause backstop at all. DV360 and Google Ads
// happen to survive it (dv360_campaign_creator forces
// entityStatus = ENTITY_STATUS_PAUSED, every Google Ads creator hardcodes
// campaign.status = PAUSED), but relying on a downstream creator to undo what
// this grid wrote is not a guarantee — so the cell is closed everywhere, and
// the appliers refuse the field too (the denylist alone is not a complete gate:
// LaunchCampaignDetails' applyFlagSuggestion stages edits by field name
// without consulting the editable set).
// Reason maps double as the denylists: the Set the grid gates on is derived
// from the keys, and the VALUES are shown to the user in the cell detail
// panel — capability honesty, the same discipline GT's descriptor keeps
// (gtReadOnlyReasons). One source, no comment/UI drift.
export const DV360_READ_ONLY_REASONS = {
  entityStatus: "Activation is the launch action's job — a review grid must never promote an entity into a live state.",
  fmt: 'Derived from the line-item type and display name — it has no storage of its own.',
  package: "The IO's package identifier — renaming it here would break how creatives and budgets are matched to this package.",
  category: "Parsed out of the IO display name's Package_Format_Category tail — edit the name instead.",
  creative: 'An asset count — creatives are managed by upload, not typed into a cell.',
  creativeFormat: "Creative sizes come from the uploaded assets' dimensions.",
  flightType: 'Line-item custom flight dates are stripped by the launcher by design; the flight is the Start/End cells.',
  impTracker: "A static 'DV360 native' label — there is no per-line-item storage behind it.",
  brandSafety: 'Campaign-scope standard exclusions, not a per-line-item value.',
  bidCeiling: 'A TTD-only column — DV360 has no max-bid slot, so there is nothing to write to.',
};
export const TTD_READ_ONLY_REASONS = {
  entityStatus: "Activation is the launch action's job — on TTD there is no pause backstop, so a Status edit would launch live and bidding.",
  fmt: 'Derived from the channel and name — it has no storage of its own.',
  package: 'The parent campaign id — an identifier, not a value.',
  creative: 'A creative count — creatives are managed elsewhere, not typed into a cell.',
  flightType: "Ad groups have no flight of their own — they inherit the campaign's Start/End.",
  impTracker: "A static 'TTD pixel' label — no per-ad-group storage behind it.",
  pixel: "TTD conversion pixels aren't in the plan template — there is nothing to write to.",
  convWindow: 'TTD ad groups carry no lookback-window storage.',
  viewability: 'Quality Alliance targeting comes from paired vendor/rate rows in the TTD template; editing the rendered summary would not safely update both fields.',
  clickURL: 'Click URLs live on the creatives on TTD, not on the ad group.',
  creativeFormat: 'Sizes ride the creatives on TTD, not the ad group.',
  interests: 'TTD has no Google-audience equivalent — this cell is always empty.',
  language: 'No language storage in the TTD template.',
  age: 'No age storage in the TTD template.',
  gender: 'No gender storage in the TTD template.',
  bidStrategy: 'Derived from Base/Max CPM — edit the Bid floor and Bid ceiling cells instead.',
  bidGoal: 'Shows the same goal as the KPI and KPI target cells — edit those instead.',
};
export const DV360_DENYLIST = new Set(Object.keys(DV360_READ_ONLY_REASONS));
export const TTD_DENYLIST = new Set(Object.keys(TTD_READ_ONLY_REASONS));

export const DV360_EDITABLE = editableFieldsFor(GROUP_COLS, DV360_DENYLIST);
export const TTD_EDITABLE = editableFieldsFor(GROUP_COLS, TTD_DENYLIST);
// GT follows the same "editable by default" derivation: every GT column plus
// the sticky name col, minus GT_NON_EDITABLE (which carries the per-entry
// justification — launcher-assigned placement ids, the creative_versions
// system count). applyGtConfigEdits writes each row key to its adgroup
// property through GT_ROW_TO_AG, falling back to an identity write, so a
// column added to GT_GROUP_COLS is persisted the moment it exists.
// GT's editable set is DESCRIPTOR-driven, not column-driven: UAT 0817
// removed several grid columns (proximity_mode, location_filter,
// neighborhood_type, targeting_identifier, special_notes) whose config values
// remain editable through the bulk-edit agent and the issue-bar jump —
// proximity_mode in particular is required for On Premise (R6), so a
// converter miss must stay fixable. Read-only fields stay excluded via the
// descriptor's own editable flags.
export const GT_EDITABLE = GT_EDITABLE_FIELDS;
// GT list-cell capabilities come from the field descriptor (goal 3), not a
// hand-maintained constant: GT_LIST_FIELDS is a projection of the same
// document that drives columns, editors and write-back keys.
export { GT_LIST_FIELDS };

// ── Parent-scaled cells (pre-commit disclosure) ──────────────────────
// Three cells show a PER-ROW SHARE of a value that is stored on the parent
// entity, and committing one writes the scaled-up TOTAL. Because the cell then
// re-renders the share, the user never sees the number they actually set: on a
// 20-ad-group TTD campaign, typing 50000 sets a $1,000,000 campaign budget and
// the cell still reads $50,000. DV360's budget cell is worse — it is derived,
// so the write divides by CPM and multiplies by siblings (at a $2 CPM, 10000 on
// a 6-line-item IO writes 30,000,000 impressions).
//
// This config drives the preview line CellDetailPanel renders live under the
// input while the user types (see scaledWritePreview there). It is a CONFIG
// rather than inline strings so the disclosure can't drift away from the
// appliers silently, and `total` calls the SAME helpers the appliers write
// through (scaleToParent / dv360ImpsFromBudget from ./sharedFieldWriters) so
// the preview cannot disagree with what is saved.
//
// Per entry:
//   scope     — what the write lands on, in the user's words ("campaign budget")
//   unit      — 'currency' | 'count', how the total (and the share) is rendered
//   sibNoun   — singular noun for the default "(share × N nouns)" tail; a
//               string, or (row) => string when it varies by row
//   total     — (share, row) => the number that will be written, or null
//   detail    — OPTIONAL (share, row) => derivation tail, overriding the
//               default "share × N nouns" one. Only a field whose write is not
//               a plain multiply needs this (DV360's derived budget).
//
// `row.siblingCount` is published by the row builders from the very same
// divisor they use for the share, so the multiply-back is exact. The renderer
// (scaledWritePreview) owns the number formatting and the default tail.

/** Render a scaled preview number per its config's `unit`. */
export const fmtScaledValue = (n, unit) => (unit === 'currency'
  ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  : Number(Math.round(n)).toLocaleString());

/** "20 ad groups" / "1 line item" */
export const pluralUnits = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;

// Rounded scale-up, for the two impression-valued cells whose parent key is an
// integer count.
const scaledCount = (share, row) => {
  const t = scaleToParent(share, row?.siblingCount);
  return t == null ? null : Math.round(t);
};

export const TTD_SCALED_FIELDS = {
  // parent.Budget — writeTtdMoney(parent, 'Budget', share × siblings)
  budget: {
    scope: 'campaign budget',
    unit: 'currency',
    sibNoun: 'ad group',
    total: (share, row) => scaleToParent(share, row?.siblingCount),
  },
  // parent.BudgetInImpressions
  imps: {
    scope: 'campaign impressions',
    unit: 'count',
    sibNoun: 'ad group',
    total: scaledCount,
  },
  // parent.DailyBudgetInImpressions
  dailyCap: {
    scope: 'campaign daily impression cap',
    unit: 'count',
    sibNoun: 'ad group',
    total: scaledCount,
  },
};

export const DV360_SCALED_FIELDS = {
  // DERIVED cell: solved back into IO impressions at the row's CPM, then
  // scaled across the IO's line items. Renders nothing without a positive CPM,
  // which is exactly when the applier no-ops too.
  budget: {
    scope: 'insertion order impressions',
    unit: 'count',
    sibNoun: 'line item',
    total: (share, row) => {
      const perRow = dv360ImpsFromBudget(share, Number(row?.cpm));
      return perRow == null ? null : scaleToParent(perRow, row?.siblingCount);
    },
    // The write is budget ÷ CPM × 1,000 × siblings, so spell that out rather
    // than the default multiply — a "$10,000 × 6 line items" tail under an
    // impression total would read as nonsense.
    detail: (share, row) => {
      const cpm = Number(row?.cpm);
      if (!Number.isFinite(cpm) || cpm <= 0) return null;
      const n = Number(row?.siblingCount);
      const base = `${fmtScaledValue(share, 'currency')} ÷ ${fmtScaledValue(cpm, 'currency')} CPM × 1,000`;
      return Number.isFinite(n) && n > 1 ? `${base} × ${pluralUnits(n, 'line item')}` : base;
    },
  },
  // IO budgetSegments[].budgetAmountInImpressions (or the package's units)
  imps: {
    scope: 'insertion order impressions',
    unit: 'count',
    sibNoun: 'line item',
    total: scaledCount,
  },
  // NOTE: DV360 `dailyCap` is deliberately absent — writeDv360DailyCap writes
  // li.pacing on the LINE ITEM itself, unscaled, so there is no hidden total
  // to disclose.
};

// Per-platform list-field capabilities — drives the cell detail panel's
// per-item add / edit / remove controls on list-shaped cells. Shape:
//   { [fieldKey]: { ops: { add, edit, remove }, addPlaceholder?, reason?, note?,
//                   lookup?: { kind } } }
// where `reason` is the user-facing line shown INSTEAD of the add input when
// add is disabled (it says why), and `note` is a hint rendered UNDER the add
// input when add is enabled. The geo cell stages ops against the two
// sub-fields the deletion flow already uses — geoInclude / geoExclude — so
// those are the keys here, not `geo`.
//
// `lookup` turns the add row into an ASYNC PICKER: the typed text is a search
// term, the panel queries the host's resolver (`onLookup(kind, query)`), and
// the chosen row is staged as `<id>|<label>` so the applier can build an entity
// carrying the platform id. Fields that need an id to launch MUST use this —
// a free-text name is unlaunchable. Add stays disabled when the host wires no
// resolver, so the capability degrades to remove-only rather than lying.
//
// GT is deliberately absent: GT list cells are comma-joined strings edited
// through the scalar editor, and the panel's `!isGt` gate keeps it that way.
export const TTD_LIST_FIELDS = {
  // Free-text 3P audience terms (MmgyAudiences.ThirdPartyAudiences) — the
  // launcher resolves them against the TTD audience taxonomy at launch, so
  // typed adds/edits are safe.
  keywords: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add keyword…' },
  // ResolvedAudiences carry platform ids (FullPath is display-only), so adds go
  // through the 3P audience search — the same one the launcher resolves MMGY
  // audiences with. Editing a resolved entry's label is still meaningless
  // (the id is what launches), so edit stays off: remove + re-pick instead.
  audiences: {
    ops: { add: true, edit: false, remove: true },
    addPlaceholder: 'Search TTD audiences…',
    lookup: { kind: 'ttd-audience' },
    note: 'Pick a result to add it with its ThirdPartyDataId',
  },
  // Geo is FREE-FORM, exactly like DV360's geo below: type a market name and the
  // launcher resolves it to a GeoSegmentId at launch time
  // (ttd_bidlist_creator.create_geo_bidlists).
  //
  // It used to be picker-backed, so a pick carried its GeoSegmentId. That failed
  // in practice: TTD rate-limits the geosegment query hard, and a typeahead
  // firing four location-type queries per keystroke earned a wall of 429s — the
  // picker returned nothing and geo became uneditable. Resolving at launch costs
  // a handful of queries per campaign instead of four per keystroke.
  //
  // This is also the plan's OWN native format: the pipeline emits geo as
  // {MetroName, CountryName} with no id, and ttdRowBuilders' labelsFromSegs has
  // always rendered names. The picker was the anomaly, not the names.
  geoInclude: {
    ops: { add: true, edit: true, remove: true },
    addPlaceholder: 'Add location…',
    note: 'Locations are resolved to TTD geo segments at launch',
  },
  geoExclude: {
    ops: { add: true, edit: true, remove: true },
    addPlaceholder: 'Add location…',
    note: 'Locations are resolved to TTD geo segments at launch',
  },
  // Id-entry: the typed value is a SupplyVendorDealId, pushed verbatim into
  // MmgyDeals.
  pmp: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add deal ID…' },
};
export const DV360_LIST_FIELDS = {
  // Adds/edits write li.contextualTargeting.includedContextualTargeting —
  // the free-text keyword list the launcher consumes.
  keywords: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add keyword…' },
  // Name-only entries are resolved against the live audience API at launch.
  audiences: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add audience name…', note: 'Added audiences are resolved by name at launch' },
  // Id-entry: googleAudienceId values.
  interests: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add Google audience ID…' },
  // Country names map to geoTargeting.included/excludedCountries; anything
  // else lands in includedOthers/excludedOthers, which the launcher resolves
  // by name (search_geo_region).
  geoInclude: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add location…' },
  geoExclude: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add location…' },
  // Id-entry: inventorySourceId values, pushed into li.pmpInventorySource.
  pmp: { ops: { add: true, edit: true, remove: true }, addPlaceholder: 'Add inventory source ID…' },
};

// Editor type per column (DV360/TTD). GT uses GT_FIELD_EDITORS from
// ./groundtruth/gtConstants. Falls back to 'text' when the column isn't
// listed. Currency fields strip $ / commas on commit; date fields use a
// native date input.
export const DV360_FIELD_EDITORS = {
  bidFloor: 'currency',
  viewability: 'number',
  start: 'date',
  end: 'date',
  cpm: 'currency',
  imps: 'number',
  budget: 'currency',
  dailyCap: 'number',
  kpiTarget: 'currency',
};
export const TTD_FIELD_EDITORS = {
  bidFloor: 'currency',
  bidCeiling: 'currency',
  start: 'date',
  end: 'date',
  cpm: 'currency',
  imps: 'number',
  budget: 'currency',
  dailyCap: 'number',
  kpiTarget: 'currency',
};
